import wsService from '../../services/websocketService';
import { getTokenChartData } from '../../services/solanatrackerService';

// Define types for TradingView integration
interface Bar {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume?: number;
}

interface HistoryMetadata {
    noData?: boolean;
    nextTime?: number;
}

interface PriceData {
    price: number;
    price_quote?: number;
    pool?: string;
    token?: string;
    time: number;
    volume?: number;  // Add optional volume property
    // Additional optional properties for flexible price extraction
    close?: number;
    p?: number;
    value?: number;
}

// Cache for the last received bars - now includes resolution in the key
const lastBarsCache = new Map<string, Bar>();

// Add a backup cache to store previous bars across reconnections - now includes resolution
const persistentBarsCache = new Map<string, Map<string, Bar>>();

// Add a global data cache to reduce API calls
interface CacheEntry {
    timestamp: number;
    data: Bar[];
    timeframe: string;
    from: number;
    to: number;
}

// Use a more efficient cache structure - nested map by token and timeframe
const historicalDataCache = new Map<string, Map<string, CacheEntry[]>>();
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes cache expiry

// Add a lastPriceMap to track prices between timeframes - now includes resolution
const lastPriceMap = new Map<string, number>();

// Add connection monitoring to detect stale WebSockets
const connectionStatus = {
    lastUpdateTime: Date.now(),
    isStale: false,
    watchdogTimer: null as NodeJS.Timeout | null,
    activeCharts: new Set<string>(),
    reconnecting: false
};

// DatafeedConfiguration implementation
const configurationData = {
    supported_resolutions: ['1S', '5S', '15S', '30S', '1', '5', '15', '30', '60', '120', '240', '360', '480', '720', 'D', 'W', 'M'],
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: true,
    supports_search: true
};

// Utility for converting resolutions
function resolutionToTimeframe(resolution: string): string {
    switch (resolution) {
        case '1S': return '1s';
        case '5S': return '5s';
        case '15S': return '15s';
        case '30S': return '30s';
        case '1': return '1m';
        case '5': return '5m';
        case '15': return '15m';
        case '30': return '30m';
        case '60': return '1h';
        case '120': return '2h';
        case '240': return '4h';
        case '360': return '6h';
        case '480': return '8h';
        case '720': return '12h';
        case 'D': return '1d';
        case 'W': return '1w';
        case 'M': return '1M';
        default: return '1h';
    }
}

// Utility to format bars for TradingView
function formatBars(data: any[]): Bar[] {
    if (!data || !Array.isArray(data)) {
        console.error('Invalid data format received:', data);
        return [];
    }

    // First pass - format the bars and do basic validation
    const formattedBars = data
        .filter(candle => {
            // Reject any candles with invalid or zero prices
            if (!candle || typeof candle !== 'object') return false;
            if (candle.open <= 0 || candle.high <= 0 || candle.low <= 0 || candle.close <= 0) {
                console.warn(`[formatBars]: Rejecting bar with zero/negative price: ${JSON.stringify(candle)}`);
                return false;
            }
            return true;
        })
        .map(candle => ({
            time: candle.time * 1000, // Convert to milliseconds for TradingView
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
            volume: candle.volume || 0
        }));

    // If we have enough data, do a second pass to filter extreme outliers
    if (formattedBars.length > 3) {
        // Calculate median price
        const prices = formattedBars.map(bar => bar.close);
        prices.sort((a, b) => a - b);
        const medianPrice = prices[Math.floor(prices.length / 2)];

        // Filter out extreme outliers (bars that deviate 90%+ from median)
        return formattedBars.filter(bar => {
            if (medianPrice > 0) {
                const deviation = Math.abs((bar.close - medianPrice) / medianPrice);
                if (deviation > 0.9) {
                    console.warn(`[formatBars]: Filtering extreme outlier: close=${bar.close}, median=${medianPrice}, deviation=${deviation.toFixed(2)}`);
                    return false;
                }
            }
            return true;
        });
    }

    return formattedBars;
}

// Function to check if we have cached data covering the requested range
function getCachedData(symbolInfo: any, timeframe: string, from: number, to: number): Bar[] | null {
    const tokenCache = historicalDataCache.get(symbolInfo.ticker);
    if (!tokenCache) return null;

    const timeframeCache = tokenCache.get(timeframe);
    if (!timeframeCache) return null;

    // Convert timestamps to milliseconds for comparison with cached data
    const fromMs = from * 1000;
    const toMs = to * 1000;

    // Look for a cache entry that covers our range
    for (const entry of timeframeCache) {
        // Skip expired cache entries
        if (Date.now() - entry.timestamp > CACHE_EXPIRY_MS) continue;

        // Check if this entry covers our requested range
        if (entry.from * 1000 <= fromMs && entry.to * 1000 >= toMs) {
            // Filter the data to just what we need
            const filteredData = entry.data.filter(bar =>
                bar.time >= fromMs && bar.time <= toMs
            );

            if (filteredData.length > 0) {
                console.log(`[getCachedData]: Using cached data for ${symbolInfo.ticker}. Found ${filteredData.length} bars`);
                return filteredData;
            }
        }
    }

    return null;
}

// Function to add data to cache
function addToCache(symbolInfo: any, timeframe: string, from: number, to: number, data: Bar[]): void {
    // Initialize caches if they don't exist
    if (!historicalDataCache.has(symbolInfo.ticker)) {
        historicalDataCache.set(symbolInfo.ticker, new Map());
    }

    const tokenCache = historicalDataCache.get(symbolInfo.ticker)!;
    if (!tokenCache.has(timeframe)) {
        tokenCache.set(timeframe, []);
    }

    // Add the new data to cache
    tokenCache.get(timeframe)!.push({
        timestamp: Date.now(),
        data,
        timeframe,
        from,
        to
    });

    // Limit cache size to prevent memory issues
    const maxCacheEntries = 10;
    const cacheEntries = tokenCache.get(timeframe)!;
    if (cacheEntries.length > maxCacheEntries) {
        // Remove oldest entries if we exceed max size
        cacheEntries.sort((a, b) => b.timestamp - a.timestamp);
        tokenCache.set(timeframe, cacheEntries.slice(0, maxCacheEntries));
    }

    console.log(`[addToCache]: Cached ${data.length} bars for ${symbolInfo.ticker} ${timeframe}`);
}

// Custom WebSocket subscribers management
interface Subscription {
    subscriptionId: string;
    handler: (data: any) => void;
    unsubscribe?: () => void;
}

const webSocketSubscribers = new Map<string, Subscription>();

// Keep track of active connections per timeframe
const activeConnections = new Map<string, number>();

// Helper function to create cache keys that include resolution
const createCacheKey = (symbolTicker: string, resolution: string): string => {
    return `${symbolTicker}_${resolution}`;
};

// Implement the TradingView datafeed interface
const customDatafeed = {
    onReady: (callback: (configuration: any) => void): void => {
        console.log('[onReady]: Initializing Solana Tracker datafeed');
        setTimeout(() => callback(configurationData));
    },

    searchSymbols: async (
        userInput: string,
        exchange: string,
        symbolType: string,
        onResultReadyCallback: (symbols: any[]) => void
    ): Promise<void> => {
        console.log('[searchSymbols]: Searching for', userInput);
        // For now just return an empty array, can be improved later
        onResultReadyCallback([]);
    },

    resolveSymbol: async (
        symbolName: string,
        onSymbolResolvedCallback: (symbolInfo: any) => void,
        onResolveErrorCallback: (reason: string) => void
    ): Promise<void> => {
        console.log('[resolveSymbol]: Resolving', symbolName);

        try {
            // Symbol information object
            const symbolInfo = {
                ticker: symbolName,
                name: symbolName,
                description: symbolName,
                type: 'crypto',
                session: '24x7',
                timezone: 'Etc/UTC',
                exchange: 'EKKOV2',
                minmov: 1,
                pricescale: 1000000, // Adjust based on token decimals
                has_intraday: true,
                has_seconds: true,  // Enable seconds data support
                has_daily: true,
                has_weekly_and_monthly: true,
                supported_resolutions: configurationData.supported_resolutions,
                volume_precision: 2,
                data_status: 'streaming',
                seconds_multipliers: ['1', '5', '15', '30'],  // Define available seconds multipliers
            };

            console.log('[resolveSymbol]: Symbol resolved', symbolName);

            // Make callback truly asynchronous as required by TradingView
            setTimeout(() => {
                onSymbolResolvedCallback(symbolInfo);
            }, 0);
        } catch (error) {
            console.error('[resolveSymbol]: Error', error);
            setTimeout(() => {
                onResolveErrorCallback('Error resolving symbol');
            }, 0);
        }
    },

    getBars: async (
        symbolInfo: any,
        resolution: string,
        periodParams: {
            from: number;
            to: number;
            firstDataRequest: boolean;
        },
        onHistoryCallback: (bars: Bar[], meta: HistoryMetadata) => void,
        onErrorCallback: (error: string) => void
    ): Promise<void> => {
        const { from, to, firstDataRequest } = periodParams;
        const timeframe = resolutionToTimeframe(resolution);

        console.log(`[getBars]: Getting data for ${symbolInfo.ticker}, ${resolution} (${timeframe}), from ${from} to ${to}, firstRequest: ${firstDataRequest}`);

        try {
            // For the first request, try to get a larger time range to reduce future API calls
            let requestFrom = from;
            let requestTo = to;

            if (firstDataRequest) {
                // Get more data than requested for small timeframes to reduce future requests
                const isSmallTimeframe = ['1s', '5s', '15s', '30s', '1m', '5m'].includes(timeframe);
                if (isSmallTimeframe) {
                    // For smaller timeframes, get 3x more historical data than requested
                    const timeRange = to - from;
                    requestFrom = from - timeRange * 2;
                } else if (timeframe === '1d' || timeframe === '1w' || timeframe === '1M') {
                    // For daily/weekly/monthly timeframes, ensure we get enough data points
                    const desiredBars = 200;
                    // Calculate the time range per bar
                    const timePerBar = timeframe === '1d' ? 86400 :
                        timeframe === '1w' ? 86400 * 7 :
                            86400 * 30;
                    // Extend the time range to ensure enough bars
                    requestFrom = from - (timePerBar * desiredBars);
                }
            }

            // Check if we have this data in cache first
            const cachedBars = getCachedData(symbolInfo, timeframe, from, to);

            if (cachedBars) {
                // Use cached data if available
                console.log(`[getBars]: Using ${cachedBars.length} cached bars for ${symbolInfo.ticker}`);

                // Cache the last bar for streaming updates
                if (cachedBars.length > 0) {
                    lastBarsCache.set(symbolInfo.ticker, cachedBars[cachedBars.length - 1]);
                }

                setTimeout(() => {
                    onHistoryCallback(cachedBars, { noData: cachedBars.length === 0 });
                }, 0);
                return;
            }

            // No cached data, fetch from API
            console.log(`[getBars]: Fetching from API for ${symbolInfo.ticker}, timeframe: ${timeframe}`);
            const result = await getTokenChartData(symbolInfo.ticker, timeframe, requestFrom, requestTo);

            if (result.status === 'error' || !result.data || result.data.length === 0) {
                console.log('[getBars]: No data for', symbolInfo.ticker);
                // Make callback truly asynchronous
                setTimeout(() => {
                    onHistoryCallback([], { noData: true });
                }, 0);
                return;
            }

            // Format bars for TradingView and filter out anomalous data (zeros and spikes)
            let bars = formatBars(result.data);

            // Filter out bad data points (zeros, extreme values)
            if (bars.length > 0) {
                // Find median price to use as reference
                const prices = bars.map(bar => bar.close);
                prices.sort((a, b) => a - b);
                const medianPrice = prices[Math.floor(prices.length / 2)];

                // Filter out zeros and extreme outliers (95%+ deviation from median)
                bars = bars.filter(bar => {
                    // Filter out zeros or near-zeros
                    if (bar.open <= 0.0000001 || bar.high <= 0.0000001 || bar.low <= 0.0000001 || bar.close <= 0.0000001) {
                        console.warn(`[getBars]: Filtering out bar with zero/near-zero value: ${JSON.stringify(bar)}`);
                        return false;
                    }

                    // If we have a median price, filter extreme outliers
                    if (medianPrice > 0) {
                        const deviation = Math.abs((bar.close - medianPrice) / medianPrice);
                        if (deviation > 0.95) { // More than 95% deviation from median
                            console.warn(`[getBars]: Filtering out extreme outlier bar: ${JSON.stringify(bar)}, deviation: ${deviation.toFixed(2)}`);
                            return false;
                        }
                    }

                    return true;
                });

                // If we filtered out too many bars (>50%), something is wrong with the data
                if (bars.length < result.data.length * 0.5) {
                    console.warn(`[getBars]: Too many anomalous bars filtered (${result.data.length - bars.length}/${result.data.length})`);
                }
            }

            // Cache the fetched data for future use
            addToCache(symbolInfo, timeframe, requestFrom, requestTo, bars);

            // Filter to just what was requested for the callback
            const requestedBars = bars.filter(bar =>
                bar.time >= from * 1000 && bar.time <= to * 1000
            );

            // Cache the last bar for streaming updates and update lastPriceMap with resolution-specific key
            if (requestedBars.length > 0) {
                const lastBar = requestedBars[requestedBars.length - 1];
                const cacheKey = createCacheKey(symbolInfo.ticker, resolution);

                lastBarsCache.set(cacheKey, lastBar);
                lastPriceMap.set(cacheKey, lastBar.close);
                console.log(`[getBars]: Cached last bar for ${cacheKey}:`, lastBar);
            }

            console.log(`[getBars]: Returned ${requestedBars.length} bars for ${symbolInfo.ticker}`);

            // Make callback truly asynchronous
            setTimeout(() => {
                onHistoryCallback(requestedBars, { noData: requestedBars.length === 0 });
            }, 0);
        } catch (error: any) {
            console.error('[getBars]: Error', error);

            // Make callback truly asynchronous
            setTimeout(() => {
                onErrorCallback(error?.message || 'Error fetching data');

                // Return an empty array on error to avoid breaking the chart
                onHistoryCallback([], { noData: true });
            }, 0);
        }
    },

    subscribeBars: (
        symbolInfo: any,
        resolution: string,
        onRealtimeCallback: (bar: Bar) => void,
        subscriberUID: string
    ): void => {
        console.log('[subscribeBars]: Subscribing', symbolInfo.ticker, 'resolution:', resolution, 'UID:', subscriberUID);

        // Create a unique cache key that includes resolution to prevent conflicts
        const cacheKey = createCacheKey(symbolInfo.ticker, resolution);

        // Create a subscription ID for price updates (shared for all resolutions of same token)
        const subscriptionId = `price:${symbolInfo.ticker}`;
        console.log(`[subscribeBars]: Joining WebSocket room: ${subscriptionId}, cache key: ${cacheKey}`);

        // Add to active charts list for connection watchdog
        connectionStatus.activeCharts.add(symbolInfo.ticker);

        // Track this connection for the current resolution
        if (!activeConnections.has(resolution)) {
            activeConnections.set(resolution, 0);
        }
        activeConnections.set(resolution, activeConnections.get(resolution)! + 1);

        // Get the proper timeframe interval in milliseconds for bar alignment
        const getTimeframeMs = (resolution: string): number => {
            // Convert resolution to milliseconds
            if (resolution === '1S') return 1000;
            if (resolution === '5S') return 5 * 1000;
            if (resolution === '15S') return 15 * 1000;
            if (resolution === '30S') return 30 * 1000;
            if (resolution === '1') return 60 * 1000;
            if (resolution === '5') return 5 * 60 * 1000;
            if (resolution === '15') return 15 * 60 * 1000;
            if (resolution === '30') return 30 * 60 * 1000;
            if (resolution === '60') return 60 * 60 * 1000;
            if (resolution === '120') return 120 * 60 * 1000;
            if (resolution === '240') return 240 * 60 * 1000;
            if (resolution === '360') return 360 * 60 * 1000;
            if (resolution === '480') return 480 * 60 * 1000;
            if (resolution === '720') return 720 * 60 * 1000;
            if (resolution === 'D') return 24 * 60 * 60 * 1000;
            if (resolution === 'W') return 7 * 24 * 60 * 60 * 1000;
            if (resolution === 'M') return 30 * 24 * 60 * 60 * 1000;
            return 60 * 1000; // Default to 1 minute
        };

        const timeframeMs = getTimeframeMs(resolution);

        // Function to align timestamp to timeframe boundaries
        const alignTimestampToTimeframe = (timestamp: number, timeframeMs: number): number => {
            return Math.floor(timestamp / timeframeMs) * timeframeMs;
        };

        // Create handler function - resolution-specific
        const handlePriceUpdate = (priceData: PriceData) => {
            try {
                // Update last activity timestamp to keep connection watchdog happy
                connectionStatus.lastUpdateTime = Date.now();

                // Enhanced validation - first check if the object is empty
                if (!priceData || typeof priceData !== 'object' || Object.keys(priceData).length === 0) {
                    // This is just a heartbeat or empty message, not an error
                    return;
                }

                // IMPROVED: More flexible price validation
                // Accept price updates even if they don't have the exact format we expect
                let price: number | undefined;

                // Try to extract price from various possible formats
                if (typeof priceData.price === 'number' && !isNaN(priceData.price)) {
                    price = priceData.price;
                } else if (typeof priceData.close === 'number' && !isNaN(priceData.close)) {
                    price = priceData.close;
                } else if (typeof priceData.p === 'number' && !isNaN(priceData.p)) {
                    price = priceData.p; // Some APIs use 'p' for price
                } else if (typeof priceData.value === 'number' && !isNaN(priceData.value)) {
                    price = priceData.value;
                }

                // Log all received messages for debugging
                console.log(`[price update debug]: Received data for ${symbolInfo.ticker} (${resolution}):`, {
                    hasPrice: price !== undefined,
                    price: price,
                    cacheKey: cacheKey,
                    originalData: priceData,
                    dataKeys: Object.keys(priceData)
                });

                // If we still don't have a valid price, skip this update but don't error
                if (price === undefined || price <= 0) {
                    console.warn(`[price update]: No valid price found in data for ${cacheKey}:`, priceData);
                    return;
                }

                // Use resolution-specific cache keys for price tracking
                const lastKnownPrice = lastPriceMap.get(cacheKey);

                // Detect and reject anomalous price values (like zeros or extreme changes)
                if (price <= 0.0000001) {
                    console.warn(`[price update]: Rejecting anomalous zero/near-zero price for ${cacheKey}: ${price}`);

                    // If we have a recent valid price for this resolution, use that instead
                    if (lastKnownPrice && lastKnownPrice > 0.0000001) {
                        console.log(`[price update]: Using last known price ${lastKnownPrice} instead of anomalous ${price} for ${cacheKey}`);
                        price = lastKnownPrice; // Replace with last known good price
                    } else {
                        return; // Skip this update entirely if no valid price available
                    }
                }

                // This is a valid price update - simplified logging
                console.log(`[price update]: ${cacheKey} $${price.toFixed(8)}`);

                // Ensure time exists and is valid
                const currentTime = priceData.time || Date.now();

                // Always track the last price for this symbol+resolution combination
                lastPriceMap.set(cacheKey, price);

                // Align the timestamp to the timeframe boundaries for proper bar formation
                const alignedTimestamp = alignTimestampToTimeframe(currentTime, timeframeMs);

                // Get the last bar for this symbol+resolution from both caches
                let lastBar = lastBarsCache.get(cacheKey);

                // If no bar in primary cache, try to get from persistent cache
                if (!lastBar && persistentBarsCache.has(symbolInfo.ticker)) {
                    const resolutionCache = persistentBarsCache.get(symbolInfo.ticker);
                    if (resolutionCache && resolutionCache.has(resolution)) {
                        lastBar = resolutionCache.get(resolution);
                        // Restore from persistent cache
                        if (lastBar) {
                            lastBarsCache.set(cacheKey, lastBar);
                            console.log(`[price update]: Restored bar from persistent cache for ${cacheKey}`);
                        }
                    }
                }

                // If we don't have a last bar, create a new one
                if (!lastBar) {
                    console.log('[price update]: No cached bar found, creating initial bar');

                    // Try to use the most recent price if available, otherwise use current price
                    const openPrice = lastPriceMap.get(cacheKey) || price;

                    const newBar: Bar = {
                        time: alignedTimestamp,
                        open: openPrice,
                        high: Math.max(openPrice, price),
                        low: Math.min(openPrice, price),
                        close: price,
                        volume: priceData.volume || 0
                    };

                    // Store in both caches
                    lastBarsCache.set(cacheKey, newBar);

                    // Also store in persistent cache
                    if (!persistentBarsCache.has(symbolInfo.ticker)) {
                        persistentBarsCache.set(symbolInfo.ticker, new Map());
                    }
                    persistentBarsCache.get(symbolInfo.ticker)!.set(resolution, newBar);

                    onRealtimeCallback(newBar);
                    console.log(`[price update]: Created new bar at ${new Date(alignedTimestamp).toISOString()} with price $${price.toFixed(8)}`);
                    return;
                }

                // Compare the aligned timestamp with the last bar's time
                if (alignedTimestamp > lastBar.time) {
                    // This is a new bar - we need to "close" the previous bar and create a new one
                    // For the previous bar, final close was the last known price
                    const finalClose = lastBar.close;

                    console.log(`[price update]: Closed bar at ${new Date(lastBar.time).toISOString()} with final close $${finalClose.toFixed(8)}`);

                    // Create a new bar - important: open price should be the last close price
                    // This ensures continuity between candles
                    const newBar: Bar = {
                        time: alignedTimestamp,
                        open: finalClose,  // New bar opens at the previous bar's close
                        high: Math.max(finalClose, price), // Initial high is max of open and current
                        low: Math.min(finalClose, price),  // Initial low is min of open and current
                        close: price,
                        volume: priceData.volume || 0
                    };

                    // Update both caches with the new bar
                    lastBarsCache.set(cacheKey, newBar);
                    if (!persistentBarsCache.has(symbolInfo.ticker)) {
                        persistentBarsCache.set(symbolInfo.ticker, new Map());
                    }
                    persistentBarsCache.get(symbolInfo.ticker)!.set(resolution, newBar);

                    // Notify the chart about the new bar
                    onRealtimeCallback(newBar);
                    console.log(`[price update]: Created new bar at ${new Date(alignedTimestamp).toISOString()}, open: $${finalClose.toFixed(8)}, close: $${price.toFixed(8)}`);
                } else if (alignedTimestamp === lastBar.time) {
                    // Update the existing bar
                    const updatedBar: Bar = {
                        ...lastBar,
                        high: Math.max(lastBar.high, price),
                        low: Math.min(lastBar.low, price),
                        close: price,
                        // Properly handle optional volume properties with fallbacks to 0
                        volume: (lastBar.volume || 0) + (priceData.volume || 0)
                    };

                    // Update both caches
                    lastBarsCache.set(cacheKey, updatedBar);
                    if (persistentBarsCache.has(symbolInfo.ticker)) {
                        persistentBarsCache.get(symbolInfo.ticker)!.set(resolution, updatedBar);
                    }

                    // Notify the chart
                    onRealtimeCallback(updatedBar);
                    console.log(`[price update]: Updated bar at ${new Date(lastBar.time).toISOString()}, high: $${updatedBar.high.toFixed(8)}, low: $${updatedBar.low.toFixed(8)}, close: $${price.toFixed(8)}`);
                } else {
                    // This is data for a bar in the past - unusual but possible
                    console.warn(`[price update]: Received outdated price data for ${new Date(alignedTimestamp).toISOString()}, current bar is at ${new Date(lastBar.time).toISOString()}`);
                }
            } catch (error) {
                console.error('[price update]: Error processing price update', error);
                // Continue chart operation despite errors
            }
        };

        // Before subscribing, check if we need to reconnect the WebSocket
        wsService.ensureConnection().then(() => {
            // When WebSocket reconnects, we need to initiate recovery
            const wsRecoveryHandler = () => {
                // If we have data in persistent cache, use it to rebuild the chart
                if (persistentBarsCache.has(symbolInfo.ticker)) {
                    const resolutionCache = persistentBarsCache.get(symbolInfo.ticker);
                    if (resolutionCache && resolutionCache.has(resolution)) {
                        const lastPersistedBar = resolutionCache.get(resolution);
                        if (lastPersistedBar) {
                            console.log(`[WebSocket recovery]: Restoring last bar for ${symbolInfo.ticker} (${resolution}) after reconnection`);
                            lastBarsCache.set(cacheKey, lastPersistedBar);

                            // Update the chart with this recovered bar to avoid gaps
                            setTimeout(() => {
                                onRealtimeCallback(lastPersistedBar);
                            }, 1000);
                        }
                    }
                }
            };

            // Store the handler and subscribe to WebSocket
            if (!webSocketSubscribers.has(subscriberUID)) {
                console.log(`[subscribeBars]: Setting up new subscription for ${subscriberUID}, room: ${subscriptionId}`);

                // Join the WebSocket room
                wsService.joinRoom(subscriptionId);
                console.log(`[subscribeBars]: Joined room ${subscriptionId}`);

                // Set up the event listener
                const unsubscribe = wsService.on(subscriptionId, handlePriceUpdate);
                console.log(`[subscribeBars]: Event listener attached for ${subscriptionId}`);

                // Add recovery handling to WebSocket reconnection events
                const recoveryUnsubscribe = wsService.on('reconnected', wsRecoveryHandler);

                // Store the subscription information
                webSocketSubscribers.set(subscriberUID, {
                    subscriptionId,
                    handler: handlePriceUpdate,
                    unsubscribe: () => {
                        console.log(`[subscribeBars]: Cleaning up subscription ${subscriberUID}`);
                        // Unsubscribe from both the price updates and reconnection events
                        unsubscribe();
                        recoveryUnsubscribe();
                    }
                });

                console.log(`[subscribeBars]: Successfully subscribed to ${subscriptionId} with UID ${subscriberUID}`);
                console.log(`[subscribeBars]: Total active subscriptions: ${webSocketSubscribers.size}`);

                // Set up a subscription health check
                setTimeout(() => {
                    if (webSocketSubscribers.has(subscriberUID)) {
                        console.log(`[subscribeBars]: Health check - subscription ${subscriberUID} still active after 30s`);
                        // Test if we're still receiving data by requesting a manual update
                        wsService.send('request_price_update', { mint: symbolInfo.ticker });
                    }
                }, 30000);

            } else {
                console.log(`[subscribeBars]: Already subscribed to ${subscriptionId} with UID ${subscriberUID}`);
            }
        }).catch(error => {
            console.error(`[subscribeBars]: Failed to ensure WebSocket connection for ${symbolInfo.ticker}:`, error);
        });
    },

    unsubscribeBars: (subscriberUID: string): void => {
        console.log('[unsubscribeBars]: Unsubscribing', subscriberUID);

        // Get the subscription
        const subscription = webSocketSubscribers.get(subscriberUID);
        if (subscription) {
            console.log(`[unsubscribeBars]: Removing subscription for ${subscription.subscriptionId}`);

            // Get the symbol from the subscription ID (format: price:SYMBOL)
            const symbolMatch = subscription.subscriptionId.match(/price:(.+)/);
            let symbol = '';
            if (symbolMatch && symbolMatch[1]) {
                symbol = symbolMatch[1];
            }

            // Parse the resolution from the subscriber ID if available
            const resolutionMatch = subscriberUID.match(/resolution:(.+?)_/);
            const resolution = resolutionMatch && resolutionMatch[1] ? resolutionMatch[1] : '';

            // Clean up resolution-specific cache entries
            if (symbol && resolution) {
                const cacheKey = createCacheKey(symbol, resolution);
                lastBarsCache.delete(cacheKey);
                lastPriceMap.delete(cacheKey);
                console.log(`[unsubscribeBars]: Cleaned up cache for ${cacheKey}`);

                // Clean up persistent cache for this resolution
                if (persistentBarsCache.has(symbol)) {
                    const resolutionCache = persistentBarsCache.get(symbol);
                    if (resolutionCache) {
                        resolutionCache.delete(resolution);
                        // If no more resolutions cached for this symbol, remove the symbol entry
                        if (resolutionCache.size === 0) {
                            persistentBarsCache.delete(symbol);
                        }
                    }
                }
            }

            // Decrement the connection count for this resolution
            if (resolution && activeConnections.has(resolution)) {
                const count = activeConnections.get(resolution)! - 1;
                if (count <= 0) {
                    activeConnections.delete(resolution);
                } else {
                    activeConnections.set(resolution, count);
                }
            }

            // Call the unsubscribe function
            if (subscription.unsubscribe) {
                subscription.unsubscribe();
            }

            // Remove from the map
            webSocketSubscribers.delete(subscriberUID);

            // Only leave the WebSocket room if no other subscriptions exist for this token
            // Check if there are any other active subscriptions for the same token
            const hasOtherSubscriptions = Array.from(webSocketSubscribers.values()).some(
                sub => sub.subscriptionId === subscription.subscriptionId
            );

            if (!hasOtherSubscriptions) {
                // No other subscriptions for this token, safe to leave the room
                wsService.leaveRoom(subscription.subscriptionId);
                console.log(`[unsubscribeBars]: Left WebSocket room ${subscription.subscriptionId} (no other subscriptions)`);

                // Remove from active charts list only if no other subscriptions
                if (symbol) {
                    connectionStatus.activeCharts.delete(symbol);
                }
            } else {
                console.log(`[unsubscribeBars]: Keeping WebSocket room ${subscription.subscriptionId} (other subscriptions exist)`);
            }

            console.log(`[unsubscribeBars]: Successfully unsubscribed from ${subscription.subscriptionId}`);
        } else {
            console.log(`[unsubscribeBars]: No subscription found for ${subscriberUID}`);
        }
    }
};

// Initialize connection watchdog
function startConnectionWatchdog() {
    if (connectionStatus.watchdogTimer) {
        clearInterval(connectionStatus.watchdogTimer);
    }

    connectionStatus.watchdogTimer = setInterval(() => {
        const now = Date.now();
        // FIXED: Much more conservative - only consider stale after 10 minutes of no activity
        // This prevents false positives during low market activity periods
        const staleThreshold = 600000; // 10 minutes instead of 5

        if (now - connectionStatus.lastUpdateTime > staleThreshold && connectionStatus.activeCharts.size > 0) {
            // Avoid multiple simultaneous reconnection attempts
            if (!connectionStatus.reconnecting && !connectionStatus.isStale) {
                connectionStatus.isStale = true;
                connectionStatus.reconnecting = true;

                console.warn(`[Connection Watchdog]: WebSocket appears stale (${Math.floor((now - connectionStatus.lastUpdateTime) / 1000)}s without updates). Forcing reconnection...`);
                console.log(`[Connection Watchdog]: Active charts: ${Array.from(connectionStatus.activeCharts).join(', ')}`);

                // Emit connection status event
                window.dispatchEvent(new CustomEvent('ekko:websocket_status', {
                    detail: { connected: false, reason: 'stale' }
                }));

                // Force WebSocket service to disconnect and reconnect
                wsService.disconnect();

                // Wait a moment before reconnecting
                setTimeout(() => {
                    console.log('[Connection Watchdog]: Attempting reconnection...');
                    wsService.connect().then(connected => {
                        if (connected) {
                            console.log('[Connection Watchdog]: Successfully reconnected');
                            // Re-join all active chart rooms
                            connectionStatus.activeCharts.forEach(chartId => {
                                console.log(`[Connection Watchdog]: Re-joining room for ${chartId}`);
                                wsService.joinRoom(`price:${chartId}`);
                            });

                            connectionStatus.isStale = false;

                            // Emit connection restored event
                            window.dispatchEvent(new CustomEvent('ekko:websocket_status', {
                                detail: { connected: true, reason: 'reconnected' }
                            }));
                        } else {
                            console.error('[Connection Watchdog]: Failed to reconnect');

                            // Emit connection failed event
                            window.dispatchEvent(new CustomEvent('ekko:websocket_status', {
                                detail: { connected: false, reason: 'reconnect_failed' }
                            }));
                        }
                        connectionStatus.reconnecting = false;
                    });
                }, 1000);
            }
        } else if (connectionStatus.isStale && now - connectionStatus.lastUpdateTime < 120000) {
            // Connection appears to be working again (activity within last 2 minutes)
            connectionStatus.isStale = false;

            // Emit connection restored event
            window.dispatchEvent(new CustomEvent('ekko:websocket_status', {
                detail: { connected: true, reason: 'activity_detected' }
            }));
        }
    }, 60000); // Check every 60 seconds instead of 30 seconds
}

// Start watchdog immediately
startConnectionWatchdog();

export default customDatafeed; 